import { RANK_STEPS } from './constants.ts'

export interface Rank {
  readonly name: string
  /** Share of the edition's best score. */
  readonly pct: number
  /** Points needed: round(best × pct). */
  readonly at: number
}

export interface Standing {
  readonly index: number
  readonly rank: Rank
  readonly next: Rank | null
  /** Points to the next rank; 0 at the top. */
  readonly toNext: number
  /** How far from this rank to the next, 0–1; 1 at the top. */
  readonly progress: number
}

/** The rank ladder for an edition whose best score is `best`. Thresholds are stored with the edition. */
export function ranksFor(best: number, steps: readonly { readonly name: string; readonly pct: number }[] = RANK_STEPS): Rank[] {
  return steps.map(({ name, pct }) => ({ name, pct, at: Math.round(best * pct) }))
}

/** Where a total sits on a ladder from ranksFor(). */
export function rankOf(total: number, ranks: readonly Rank[]): Standing {
  let index = 0
  ranks.forEach((r, i) => { if (total >= r.at) index = i })
  const rank = ranks[index]
  const next = ranks[index + 1] ?? null
  return {
    index,
    rank,
    next,
    toNext: next ? next.at - total : 0,
    progress: next ? (total - rank.at) / (next.at - rank.at) : 1,
  }
}

/**
 * A result as a sentence in two parts, the rank's part last: ["Sam made the", "Front Page"], but
 * ["Sam", "stopped the presses"] for the top rank, which "made the" doesn't fit. A shared link's
 * title runs them together; its picture sets the rank's part large.
 */
export function rankPhrase(name: string, rank: string): readonly [lead: string, rank: string] {
  return rank === RANK_STEPS[RANK_STEPS.length - 1].name ? [name, 'stopped the presses'] : [`${name} made the`, rank]
}

/** rankPhrase as one line: "Sam made the Front Page", "Sam stopped the presses". */
export const rankLine = (name: string, rank: string): string => rankPhrase(name, rank).join(' ')
